import { z } from "zod";

// ---------------------------------------------------------------------------
// Outbox: messages to employees over an external channel
//
// GRA-251 wires a real worker: records move queued -> sending -> sent|failed.
// There is still no WhatsApp provider, so "whatsapp" records are delivered by
// email until one is chosen (see `OUTBOX_WHATSAPP_ENABLED` in
// packages/jobs/src/trigger/outbox-deliver.ts). Stored at:
//   Businesses/{businessId}/outbox/{messageId}
// ---------------------------------------------------------------------------

export const OutboxChannel = z.enum(["whatsapp", "slack", "teams", "email"]);
export type OutboxChannel = z.infer<typeof OutboxChannel>;

export const OutboxStatus = z.enum(["queued", "sending", "sent", "failed"]);
export type OutboxStatus = z.infer<typeof OutboxStatus>;

/**
 * Meta's WhatsApp status webhook (GRA-286): per-recipient detail layered on
 * top of `OutboxRecipient.deliveredAt`/`deliveryError`, which only record
 * our own send attempt. "sent" here just means Meta accepted the message -
 * it is not the same "sent" as `OutboxStatus`, which the UI already shows
 * once our send call succeeds.
 */
export const WhatsAppMessageStatus = z.enum(["sent", "delivered", "read", "failed"]);
export type WhatsAppMessageStatus = z.infer<typeof WhatsAppMessageStatus>;

/** The three message templates from the sketch. */
export const OutboxTemplate = z.enum(["vanlig", "kort", "pepp"]);
export type OutboxTemplate = z.infer<typeof OutboxTemplate>;

/** Automatic retries (SDK-driven, see outbox-deliver.ts) before a record is terminally "failed". */
export const MAX_OUTBOX_DELIVERY_ATTEMPTS = 3;

/** A "sending" claim older than this is assumed crashed and can be re-claimed. */
export const OUTBOX_SENDING_STALE_MS = 10 * 60 * 1000;

export const OutboxRecipient = z.object({
  memberId: z.string().min(1),
  name: z.string().min(1),
  phone: z.string().min(1).nullable().default(null),
  /** The message rendered for this recipient. */
  message: z.string().min(1).max(2000),
  /** Resolved (and cached) at delivery time from the member roster; null until then. */
  email: z.string().nullable().default(null),
  /** Set once this recipient's message has actually gone out (or was simulated, see `simulated`). */
  deliveredAt: z.string().nullable().default(null),
  /** Set when the last delivery attempt for this recipient failed; cleared on success. */
  deliveryError: z.string().nullable().default(null),
  /**
   * True when a non-prod allowlist guard blocked the real send (GRA-251/GRA-254):
   * treated as "delivered" for the record's status so staging doesn't show a
   * false "Misslyckades", but no email actually went out.
   */
  simulated: z.boolean().default(false),
  /** The provider's id for this recipient's message (WhatsApp's WAMID today). Null until a real send succeeds. */
  providerMessageId: z.string().nullable().default(null),
  /** Latest status Meta's webhook reported for `providerMessageId` (GRA-286). Extra detail only - never downgrades `OutboxRecord.status`. */
  whatsappStatus: WhatsAppMessageStatus.nullable().default(null),
  /** When `whatsappStatus` was last updated. */
  whatsappStatusAt: z.string().nullable().default(null),
  /** Meta's failure detail when `whatsappStatus` is "failed" (e.g. undelivered after acceptance). Distinct from `deliveryError`, which is about our own send call. */
  whatsappStatusError: z.string().nullable().default(null),
});
export type OutboxRecipient = z.infer<typeof OutboxRecipient>;
/**
 * What a caller provides before the delivery-tracking fields exist (the
 * worker-only fields all default on the server). Use this, not
 * `OutboxRecipient`, when building the payload for `enqueue`.
 */
export type OutboxRecipientDraft = z.input<typeof OutboxRecipient>;

export interface OutboxRecord {
  id: string;
  businessId: string;
  channel: OutboxChannel;
  /** What the send was about, e.g. "Dagens uppgifter". */
  subject: string;
  template: OutboxTemplate | null;
  recipients: OutboxRecipient[];
  status: OutboxStatus;
  createdBy: string | null;
  createdAt: string | null;
  sentAt: string | null;
  providerMessageId: string | null;
  error: string | null;
  /** Incremented every time a delivery attempt is claimed; capped by `MAX_OUTBOX_DELIVERY_ATTEMPTS`. */
  attempts: number;
  lastAttemptAt: string | null;
  /** Channel actually used once `status` is "sent" (may differ from `channel`, e.g. whatsapp -> email fallback). */
  deliveredVia: OutboxChannel | null;
}

export const EnqueueOutboxInput = z.object({
  businessId: z.string().min(1),
  channel: OutboxChannel.default("whatsapp"),
  subject: z.string().min(1).max(120),
  template: OutboxTemplate.nullable().optional(),
  recipients: z.array(OutboxRecipient).min(1).max(100),
});
export type EnqueueOutboxInput = z.infer<typeof EnqueueOutboxInput>;
/** The payload callers build before defaults are applied - see `OutboxRecipientDraft`. */
export type EnqueueOutboxDraft = z.input<typeof EnqueueOutboxInput>;

export const ListOutboxInput = z.object({
  businessId: z.string().min(1),
  status: OutboxStatus.optional(),
});
export type ListOutboxInput = z.infer<typeof ListOutboxInput>;

export const RetryOutboxInput = z.object({
  businessId: z.string().min(1),
  outboxId: z.string().min(1),
});
export type RetryOutboxInput = z.infer<typeof RetryOutboxInput>;

/**
 * Render one of the sketch's templates. `tasks` are the bullet lines; `from`
 * is the sender's first name.
 */
export function renderOutboxTemplate(
  template: OutboxTemplate,
  recipientFirstName: string,
  tasks: string[],
  from: string,
): string {
  const bullets = tasks.map((t) => `• ${t}`).join("\n");
  switch (template) {
    case "kort":
      return `${recipientFirstName}, idag: ${tasks.join(", ")}. Svara 1 när klart.`;
    case "pepp":
      return `God morgon ${recipientFirstName}! Idag gör vi skillnad:\n${bullets}\n\nDu fixar det. Svara 1 när det är klart!`;
    case "vanlig":
    default:
      return `Hej ${recipientFirstName}! Här är dina uppgifter för idag:\n${bullets}\n\nSvara 1 när allt är klart. /${from}`;
  }
}
